import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, type Api } from "../../../services/api";
import { displayToast } from "../../../components/app/AppToast";
import {
  cancelSpeechStream,
  speechStreamUrl,
  streamSpeechToObjectUrl,
} from "../lib/streamSpeech";
import { isIOSApp } from "../../../tools/ios";
import { iosAudioCanPlayStream } from "../../../tools/ios/audio";
import type { TrackSource } from "../../../domains/app/contexts/audio.context";
import type { ArticleDetail } from "./useArticleData";

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

type ArticleAudioModel = ArticleDetail | ArticleListItem;

/** A playable track: where the bytes are, and how they arrive. */
export type ArticleAudioTrack = TrackSource & { url: string };

// Polling budget for the native-iOS path in `synthesize`. A full IMRAD article
// is a handful of sequential provider calls, so allow generously before the
// reader is told it failed.
const SPEECH_POLL_INTERVAL_MS = 2000;
const SPEECH_POLL_TIMEOUT_MS = 180_000;

// The article whose audio the reader last asked for. Starting a new request
// (another article, or a second tap) supersedes any poll still running, so a
// URL that resolves late never starts playing over the article the reader has
// moved on to — the same supersede-on-new-request contract that
// `streamSpeechToObjectUrl` enforces with its abort controller.
let pendingSpeechArticleId: string | null = null;

/**
 * Abandons `articleId`'s request: it resolves `superseded` at its next step
 * and its stream, if one runs, stops. Opening that stream would otherwise
 * abort and revoke the audio the reader went back to.
 */
export function cancelSpeechRequest(articleId: string): void {
  if (pendingSpeechArticleId === articleId) pendingSpeechArticleId = null;
  cancelSpeechStream(articleId);
}

/** How a request for an article's audio ended. */
export type ArticleSpeechRequest =
  | { status: "ready"; track: ArticleAudioTrack }
  | { status: "superseded" }
  | { status: "failed" };

function ready(track: ArticleAudioTrack): ArticleSpeechRequest {
  return { status: "ready", track };
}

/**
 * Resolves a playable track for an article, synthesizing it when no audio
 * exists yet: the persisted file, else the live stream a browser or a
 * streaming shell can consume, else the file the native iOS shell waits for.
 * `superseded` when a later request for another article replaced this one.
 * On `failed`, the caller tells the reader.
 */
export async function requestArticleSpeech(
  article: ArticleAudioModel
): Promise<ArticleSpeechRequest> {
  // Already synthesized → play the persisted public URL directly.
  const cachedUrl = article.speech !== null ? article.speech.url : null;
  if (cachedUrl !== null) return ready({ url: cachedUrl, kind: "file" });

  const requestedId = article.id;
  pendingSpeechArticleId = requestedId;
  try {
    // Another listener's stream (which the API persists) or the publish-time
    // job may have produced the audio since this article was fetched.
    // A static GCS URL beats a live stream on every axis: it is seekable,
    // browser-cacheable, and playable by the native shell.
    const speech = await apiClient.get("/webapp/private/articles/:id/speech", {
      param: { id: requestedId },
    });
    if (speech.status === 200 && speech.body.url !== null) {
      return ready({ url: speech.body.url, kind: "file" });
    }

    // Abandoned while the lookup ran: a stream now would cut off the audio
    // the reader went back to.
    if (pendingSpeechArticleId !== requestedId) {
      return { status: "superseded" };
    }

    // Browsers can consume the live provider stream; the API tees it into
    // GCS, so this slow path runs at most once per article.
    if (!isIOSApp()) {
      const objectUrl = await streamSpeechToObjectUrl(requestedId);
      return ready({ url: objectUrl, kind: "file" });
    }

    // Shells that can stream consume the endpoint directly — playback starts
    // in a second or two instead of after the whole synthesis.
    // The stream cannot seek; once its synthesis is whole, the player
    // switches to the file the API saved it to, at the same position.
    if (await iosAudioCanPlayStream()) {
      const streamId = crypto.randomUUID();
      return ready({
        url: speechStreamUrl(requestedId, { stream_id: streamId }),
        kind: "live-stream",
        articleId: requestedId,
        streamId,
      });
    }

    // The native iOS shell cannot: `streamSpeechToObjectUrl` hands back a
    // `blob:` URL, `URL(string:)` accepts the scheme so
    // `AudioManager.play(url:)` reports success, but AVPlayer never loads
    // it. `getDuration` then answers -1 forever and
    // `InternalAudioPlayer.setTrack` spins in `while (duration <= 0)`, so
    // the autoplay callback never fires and the player sits on Play. Wait
    // for the real file instead.
    const dispatched = await apiClient.post(
      "/webapp/private/articles/:id/synthesize",
      { param: { id: requestedId } }
    );
    if (dispatched.body.url !== null) {
      return ready({ url: dispatched.body.url, kind: "file" });
    }

    const deadline = Date.now() + SPEECH_POLL_TIMEOUT_MS;
    for (;;) {
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, SPEECH_POLL_INTERVAL_MS);
      });
      if (pendingSpeechArticleId !== requestedId) {
        return { status: "superseded" };
      }

      const polled = await apiClient.get(
        "/webapp/private/articles/:id/speech",
        { param: { id: requestedId } }
      );
      if (polled.status === 200 && polled.body.url !== null) {
        return ready({ url: polled.body.url, kind: "file" });
      }
      if (Date.now() > deadline) return { status: "failed" };
    }
  } catch {
    // A cancelled request's stream rejects on its abort: not a failure.
    return pendingSpeechArticleId === requestedId
      ? { status: "failed" }
      : { status: "superseded" };
  }
}

export function useArticleAudio(
  article: ArticleAudioModel | null,
  _slug: string
) {
  const { t } = useTranslation();
  const [isSynthesizing, setIsSynthesizing] = useState(false);

  // No warm-up on article open. It dispatched a background synthesis 1 s
  // after opening, which a Listen tap then duplicated with a live stream: two
  // billed syntheses for one listen, plus one for every reader who never
  // tapped Listen. The live stream starts in about 1.5 s, so synthesis now
  // happens only when a reader asks for audio (and at publish, for B2B rooms).
  const synthesize =
    useCallback(async (): Promise<ArticleAudioTrack | null> => {
      if (article === null) return null;
      if (article.speech !== null) {
        return { url: article.speech.url, kind: "file" };
      }

      setIsSynthesizing(true);
      const request = await requestArticleSpeech(article);
      setIsSynthesizing(false);
      switch (request.status) {
        case "ready":
          return request.track;
        case "superseded":
          return null;
        case "failed":
          displayToast(t("Couldn't play audio. Try again."), "error");
          return null;
        default:
          throw new Error(request satisfies never);
      }
    }, [article, t]);

  return { isSynthesizing, synthesize };
}
